import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { serviceDeliveryAPI, briefingFileUrl, clientWelcomeAPI } from '../lib/api';
import ClientContentCalendar from './ClientContentCalendar';
import ClientMeetings from './ClientMeetings';
import BuyerPersonaSection from './BuyerPersonaSection';
import BriefingForm from './BriefingForm';
import BusinessInfoSection from './BusinessInfoSection';
import SocialProofSection from './SocialProofSection';
import BrandKitSection from './BrandKitSection';
import MarketResearchSection from './MarketResearchSection';
import BusinessStrategySection from './BusinessStrategySection';
import ContentStrategySection from './ContentStrategySection';
import AdsStrategySection from './AdsStrategySection';
import AdsCampaignsSection from './AdsCampaignsSection';
import ContentLibrarySection from './ContentLibrarySection';
import CopiesSection from './CopiesSection';
import ObjectivesSection from './ObjectivesSection';
import ReportsSection from './ReportsSection';
import BillingIncidentsSection from './BillingIncidentsSection';
import WeeklyActionsSection from './WeeklyActionsSection';
import ClientArchiveControls from './ClientArchiveControls';
import DriveFoldersSection from './DriveFoldersSection';
import { FolderOpen as FolderOpenIcon, Heart as HeartIcon, ListChecks as ListChecksIcon, Megaphone as MegaphoneIcon } from 'lucide-react';
import ClientWelcomeSetup from './ClientWelcomeSetup';
import ClientPortalActivity from './ClientPortalActivity';
import { ViewClientPortalButton } from './ViewClientPortalButton';
import { Activity as ActivityIcon, Send } from 'lucide-react';
import { ClientChecklistsSection } from './ClientChecklistsSection';
import { AdsApprovalsManager } from './AdsApprovalsManager';
import { useAuthStore } from '../store';
import { BRIEFING_STATUS } from '../lib/briefingConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Building,
  Users,
  FileText,
  Calendar,
  Target,
  Megaphone,
  Image,
  MessageSquare,
  BarChart3,
  Lightbulb,
  PenTool,
  Video,
  BookOpen,
  CheckCircle2,
  Clock,
  Plus,
  Search,
  ChevronRight,
  Globe,
  Instagram,
  Edit,
  ArrowLeft,
  Palette,
  TrendingUp,
  Briefcase,
  ClipboardList,
  Save,
  X,
  Loader2,
  Trash2,
  Upload,
  FileIcon,
  Download,
  Share2,
  Mail,
  Receipt,
  Link2,
  AlertTriangle,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Sección de integraciones por cliente: vincular contacto de Quipu (facturas) y cuenta de Meta (anuncios)
function ClientIntegrationsSection({ client }) {
  const clientId = client._id || client.id;
  const [contacts, setContacts] = useState([]);
  const [loadingC, setLoadingC] = useState(true);
  const [quipuId, setQuipuId] = useState(client.quipu_contact_id || '');
  const [search, setSearch] = useState('');
  const [savingQ, setSavingQ] = useState(false);

  useEffect(() => {
    setLoadingC(true);
    serviceDeliveryAPI.getAllQuipuContacts()
      .then((r) => setContacts(r?.data || []))
      .catch(() => setContacts([]))
      .finally(() => setLoadingC(false));
  }, [clientId]);

  const contactName = (qc) => {
    const a = qc.attributes || {};
    return a.legal_name || a.name || a.commercial_name || `Contacto ${qc.id}`;
  };
  const filtered = contacts.filter((qc) => contactName(qc).toLowerCase().includes(search.trim().toLowerCase()));
  const selectedContact = contacts.find((qc) => String(qc.id) === String(quipuId));

  const saveQuipu = async (id) => {
    setQuipuId(id); setSavingQ(true);
    try { await serviceDeliveryAPI.setQuipuContact(clientId, id || null); toast.success(id ? 'Contacto de Quipu vinculado' : 'Vínculo de Quipu eliminado'); }
    catch (e) { toast.error(e.message || 'Error al vincular'); }
    setSavingQ(false);
  };

  return (
    <div className="space-y-6" data-testid="client-integrations-section">
      {/* FACTURACIÓN (Quipu) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5" /> Facturación (Quipu)</CardTitle>
          <CardDescription>Vincula este cliente con su contacto de Quipu. Una vez vinculado, el cliente verá SOLO sus facturas en el portal y podrá descargarlas en PDF.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {selectedContact && (
            <div className="flex items-center justify-between gap-3 rounded-lg border bg-emerald-50 px-3 py-2" data-testid="quipu-linked-badge">
              <div className="min-w-0">
                <p className="text-xs text-emerald-700 uppercase font-semibold">Vinculado a</p>
                <p className="text-sm font-medium truncate">{contactName(selectedContact)}</p>
              </div>
              <Button size="sm" variant="outline" disabled={savingQ} onClick={() => saveQuipu('')} data-testid="quipu-unlink-btn">
                <X className="w-4 h-4 mr-1" /> Quitar
              </Button>
            </div>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Buscar contacto de Quipu por nombre..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="quipu-search-input" />
          </div>
          {loadingC ? (
            <div className="py-6 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <ScrollArea className="h-64 rounded-lg border">
              <div className="divide-y">
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No hay contactos que coincidan.</p>
                ) : filtered.map((qc) => {
                  const isSel = String(qc.id) === String(quipuId);
                  const a = qc.attributes || {};
                  return (
                    <button
                      key={qc.id}
                      disabled={savingQ}
                      onClick={() => saveQuipu(String(qc.id))}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 transition-colors ${isSel ? 'bg-primary/10' : 'hover:bg-muted'}`}
                      data-testid={`quipu-contact-option-${qc.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{contactName(qc)}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.tax_id || ''}{a.town ? ` · ${a.town}` : ''}</p>
                      </div>
                      {isSel && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Acceso al portal: sin invitar / invitado sin entrar / última visita
const timeAgo = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return m <= 1 ? 'hace un momento' : `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'ayer' : d < 30 ? `hace ${d} días` : new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};
function PortalAccessBadge({ access, id }) {
  if (!access?.invited) return <Badge variant="outline" className="text-xs text-muted-foreground" data-testid={`portal-access-${id}`}>Sin invitar al portal</Badge>;
  if (!access.last_seen_at) return <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50" data-testid={`portal-access-${id}`}>Invitado · aún no ha entrado</Badge>;
  return (
    <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50" title={new Date(access.last_seen_at).toLocaleString('es-ES')} data-testid={`portal-access-${id}`}>
      Entró al portal {timeAgo(access.last_seen_at)}
    </Badge>
  );
}

// Aviso si el cliente contestó distinto en el Briefing antiguo y en Bienvenida (manda Bienvenida)
const LEGACY_PAGO = { recibo: 'sepa', transferencia: 'transferencia' };
const PAGO_LABEL = { sepa: 'SEPA (recibo)', transferencia: 'Transferencia' };
function PaymentConflictWarning({ clientData }) {
  const legacy = LEGACY_PAGO[String(clientData?.briefing_data?.info_basica?.forma_pago || '').toLowerCase()];
  const current = clientData?.forma_pago;
  if (!legacy || !current || legacy === current) return null;
  return (
    <p className="mt-2 text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1 inline-flex items-center gap-1" data-testid="payment-conflict-warning">
      <AlertTriangle className="w-3.5 h-3.5" />
      Forma de pago distinta: en el Briefing puso «{PAGO_LABEL[legacy]}» y en Bienvenida «{PAGO_LABEL[current]}». Manda la de Bienvenida; confírmalo con el cliente.
    </p>
  );
}

// Secciones del cliente
const seccionesCliente = [
  { id: 'bienvenida', nombre: 'Bienvenida del portal', icon: HeartIcon, ceoOnly: true },
  { id: 'actividad', nombre: 'Actividad en su portal', icon: ActivityIcon, ceoOnly: true },
  { id: 'checklists', nombre: 'Checklists internos', icon: ListChecksIcon, ceoOnly: true },
  { id: 'ads_creativos', nombre: 'Aprobación de creativos', icon: MegaphoneIcon, ceoOnly: true },
  { id: 'briefing', nombre: 'Briefing', icon: FileText },
  { id: 'plan-social-media', nombre: 'Plan de Social Media', icon: Share2 },
  { id: 'calendario', nombre: 'Calendario de Contenido', icon: Calendar },
  { id: 'info', nombre: 'Información Cliente', icon: Globe, ceoOnly: true },
  { id: 'reuniones', nombre: 'Reuniones', icon: MessageSquare },
  { id: 'buyer-persona', nombre: 'Buyer Persona', icon: Users },
  { id: 'negocio', nombre: 'Info Negocio', icon: Briefcase },
  { id: 'prueba-social', nombre: 'Prueba Social', icon: CheckCircle2 },
  { id: 'branding', nombre: 'Branding / Kit Marca', icon: Palette },
  { id: 'drive', nombre: 'Carpetas de Drive', icon: FolderOpenIcon },
  { id: 'investigacion', nombre: 'Investigación Mercado', icon: BarChart3 },
  { id: 'oferta', nombre: 'Oferta', icon: Target },
  { id: 'estrategia-negocio', nombre: 'Estrategia Negocio', icon: TrendingUp },
  { id: 'estrategia-ads', nombre: 'Estrategia Ads', icon: Megaphone },
  { id: 'estrategia-contenido', nombre: 'Estrategia Contenido', icon: PenTool },
  { id: 'anuncios', nombre: 'ADS', icon: Video },
  { id: 'contenido', nombre: 'Contenido', icon: Image },
  { id: 'copies', nombre: 'Copies', icon: BookOpen },
  { id: 'objetivos', nombre: 'Objetivos', icon: Target },
  { id: 'informes', nombre: 'Informes Mensuales', icon: BarChart3 },
  { id: 'especificaciones', nombre: 'Especificaciones', icon: ClipboardList, ceoOnly: true },
  { id: 'incidencias-cobro', nombre: 'Incidencias de Cobro', icon: Receipt, ceoOnly: true },
  { id: 'integraciones', nombre: 'Facturación', icon: Receipt, ceoOnly: true },
  { id: 'acciones', nombre: 'Acciones Semanales', icon: CheckCircle2 },
];

const estadoConfig = {
  activo: { label: 'Activo', color: 'bg-emerald-100 text-emerald-700' },
  pausado: { label: 'Pausado', color: 'bg-amber-100 text-amber-700' },
  finalizado: { label: 'Finalizado', color: 'bg-gray-100 text-gray-700' },
  inactivo: { label: 'Dado de baja', color: 'bg-slate-200 text-slate-700' },
};

// Componente de sección editable
function EditableSection({ title, icon: Icon, sectionId, data, onSave, children, renderEdit }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditData(data || {});
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(sectionId, editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setEditData(data || {});
    setIsEditing(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5" />}
          {title}
        </CardTitle>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Guardar
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4 mr-1" />
              Editar
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? renderEdit(editData, setEditData) : children}
      </CardContent>
    </Card>
  );
}

// Componente para editar arrays de strings
function EditableList({ items = [], onChange, placeholder = "Nuevo elemento" }) {
  const [newItem, setNewItem] = useState('');

  const addItem = () => {
    if (newItem.trim()) {
      onChange([...(items || []), newItem.trim()]);
      setNewItem('');
    }
  };

  const removeItem = (index) => {
    onChange((items || []).filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {(items || []).map((item, i) => (
          <Badge key={i} variant="secondary" className="flex items-center gap-1 py-1">
            {item}
            <button onClick={() => removeItem(i)} className="ml-1 hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={placeholder}
          onKeyPress={(e) => e.key === 'Enter' && addItem()}
          className="flex-1"
        />
        <Button type="button" size="sm" onClick={addItem}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Componente de subida de archivos
function FileUploadSection({ clientId, sectionId, archivos = [], onUploadComplete, highlightFirst }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (sectionId === 'plan-social-media' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('El Plan de Social Media tiene que ser un PDF');
      e.target.value = '';
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Archivo demasiado grande (máx 100 MB)');
      e.target.value = '';
      return;
    }
    try {
      await serviceDeliveryAPI.uploadFile(clientId, sectionId, file);
      if (onUploadComplete) onUploadComplete();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error al subir el archivo: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) return;
    
    setDeleting(fileId);
    try {
      await serviceDeliveryAPI.deleteFile(clientId, fileId, sectionId);
      if (onUploadComplete) onUploadComplete();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error al eliminar el archivo: ' + error.message);
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (tipo) => {
    if (tipo?.startsWith('image/')) return '🖼️';
    if (tipo?.includes('pdf')) return '📄';
    if (tipo?.includes('word') || tipo?.includes('document')) return '📝';
    if (tipo?.includes('excel') || tipo?.includes('spreadsheet')) return '📊';
    if (tipo?.includes('video')) return '🎬';
    return '📁';
  };

  return (
    <div className="mt-4 p-4 rounded-lg border bg-muted/30" data-testid={`file-upload-section-${sectionId}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold flex items-center gap-2">
          <Upload className="w-4 h-4" />
          Archivos Adjuntos
        </h4>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={sectionId === 'plan-social-media' ? 'application/pdf,.pdf' : undefined}
            onChange={handleFileChange}
            className="hidden"
            id={`file-input-${sectionId}`}
            data-testid={`file-input-${sectionId}`}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid={`upload-btn-${sectionId}`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Subiendo...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                Subir Archivo
              </>
            )}
          </Button>
        </div>
      </div>

      {(archivos && archivos.length > 0) ? (
        <div className="space-y-2">
          {archivos.map((archivo, idx) => (
            <div
              key={archivo.id}
              className={`flex items-center justify-between p-3 rounded-lg bg-background border ${highlightFirst && idx === 0 ? 'border-emerald-300' : ''}`}
              data-testid={`file-item-${archivo.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{getFileIcon(archivo.tipo)}</span>
                <div>
                  <p className="font-medium text-sm">{archivo.nombre}
                    {highlightFirst && idx === 0 && <Badge className="ml-2 bg-emerald-100 text-emerald-700" data-testid="plan-current-badge">{highlightFirst}</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(archivo.tamano)} • {new Date(archivo.fecha).toLocaleDateString('es-ES')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={serviceDeliveryAPI.getFileUrl(archivo.path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-muted rounded"
                  data-testid={`download-btn-${archivo.id}`}
                >
                  <Download className="w-4 h-4 text-primary" />
                </a>
                <button
                  onClick={() => handleDelete(archivo.id)}
                  disabled={deleting === archivo.id}
                  className="p-2 hover:bg-red-100 rounded text-red-500"
                  data-testid={`delete-btn-${archivo.id}`}
                >
                  {deleting === archivo.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground">
          <FileIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No hay archivos adjuntos</p>
          <p className="text-xs">Haz clic en "Subir Archivo" para añadir documentos</p>
        </div>
      )}
    </div>
  );
}

export function ServiceDeliveryPage() {
  const { user } = useAuthStore();
  const isCEO = user?.role === 'ceo';
  const [clients, setClients] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [activeSection, setActiveSection] = useState('briefing');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [deletingClient, setDeletingClient] = useState(null);
  const [initialPostIdToOpen, setInitialPostIdToOpen] = useState(null);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [editClientDraft, setEditClientDraft] = useState(null);
  const editEmailInvalid = !!editClientDraft?.email?.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editClientDraft.email.trim());
  const [discordTest, setDiscordTest] = useState(null);
  const [discordAdsTest, setDiscordAdsTest] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);  // { ok, email, email_sent, setup_link, email_error }
  const [inviting, setInviting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [newClient, setNewClient] = useState({
    nombre: '',
    contacto: '',
    telefono: '',
    email: '',
    servicio: '',
    estado: 'activo',
    fechaInicio: '',
    observaciones: ''
  });

  const serviciosOptions = [
    'Redes Sociales',
    'Redes Sociales + Ads',
    'Ads',
    'Marketing Digital',
    'Asesoría',
    'Otros'
  ];

  // Load clients
  useEffect(() => {
    loadClients();
  }, []);

  // Auto-open client + post from PublicationsPage navigation
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const st = { ...(location.state || {}) };
    if (!st.openClientId && sp.get('client')) {
      st.openClientId = sp.get('client');
      st.openPostId = sp.get('post');
      st.openSection = sp.get('section') || 'calendario';
    }
    if (st.openClientId && clients.length > 0) {
      const target = clients.find((c) => (c._id === st.openClientId || c.id === st.openClientId));
      if (target) {
        setSelectedCliente(target);
        setActiveSection(st.openSection || 'calendario');
        if (st.openPostId) setInitialPostIdToOpen(st.openPostId);
      } else {
        // Cliente dado de baja (no está en la lista del día a día): abrir su ficha igualmente
        serviceDeliveryAPI.getClient(st.openClientId).then((c) => {
          if (c) { setSelectedCliente(c); setActiveSection(st.openSection || 'calendario'); if (st.openPostId) setInitialPostIdToOpen(st.openPostId); }
        }).catch(() => {});
      }
      // Clear state so it doesn't fire again on re-render
      navigate('/service-delivery', { replace: true, state: {} });
    }
  }, [location.state, location.search, clients, navigate]);

  // Load client detail when selected
  useEffect(() => {
    if (selectedCliente) {
      loadClientDetail(selectedCliente._id || selectedCliente.id);
    }
  }, [selectedCliente]);

  const loadClients = async (inactive = showInactive) => {
    setLoading(true);
    try {
      const data = await serviceDeliveryAPI.getClients(inactive ? 'inactivo' : undefined);
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
    setLoading(false);
  };

  const loadClientDetail = async (clientId) => {
    try {
      const data = await serviceDeliveryAPI.getClient(clientId);
      setClientData(data);
    } catch (error) {
      console.error('Error loading client detail:', error);
    }
  };

  const handleCreateClient = async () => {
    // Validación del campo obligatorio
    if (!newClient.nombre.trim()) {
      alert('El nombre de la empresa es obligatorio');
      return;
    }

    setCreatingClient(true);
    try {
      const created = await serviceDeliveryAPI.createClient(newClient);
      setClients([...clients, created]);
      setShowNewClientForm(false);
      setNewClient({
        nombre: '',
        contacto: '',
        telefono: '',
        email: '',
        servicio: '',
        estado: 'activo',
        fechaInicio: '',
        observaciones: ''
      });
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Error al crear el cliente');
    }
    setCreatingClient(false);
  };

  const handleDeleteClient = async (clientId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Estás seguro de eliminar este cliente? Esta acción no se puede deshacer.')) {
      return;
    }
    
    setDeletingClient(clientId);
    try {
      await serviceDeliveryAPI.deleteClient(clientId);
      setClients(clients.filter(c => (c._id || c.id) !== clientId));
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Error al eliminar el cliente');
    }
    setDeletingClient(null);
  };

  const handleSaveSection = async (sectionId, data) => {
    const clientId = clientData._id || clientData.id;
    await serviceDeliveryAPI.updateSection(clientId, sectionId, data);
    // Reload client data
    await loadClientDetail(clientId);
  };

  const filteredClientes = clients.filter(c =>
    c.nombre?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Vista de lista de clientes
  if (!selectedCliente) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
        data-testid="service-delivery-page"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Entrega de Servicios</h1>
            <p className="text-muted-foreground mt-1">Gestión completa de clientes</p>
          </div>
          <Button className="gradient-primary text-white" onClick={() => setShowNewClientForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </motion.div>

        {/* Activos / dados de baja */}
        <motion.div variants={itemVariants} className="flex rounded-lg border bg-background p-0.5 w-fit text-sm" data-testid="clients-status-tabs">
          <button onClick={() => { setShowInactive(false); loadClients(false); }} className={`px-4 py-1.5 rounded-md font-medium transition-colors ${!showInactive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="clients-tab-active">Clientes en curso</button>
          <button onClick={() => { setShowInactive(true); loadClients(true); }} className={`px-4 py-1.5 rounded-md font-medium transition-colors ${showInactive ? 'bg-slate-700 text-white' : 'text-muted-foreground hover:bg-muted'}`} data-testid="clients-tab-inactive">Clientes inactivos (dados de baja)</button>
        </motion.div>

        {/* Stats */}
        {!showInactive && (
        <motion.div variants={itemVariants} className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {clients.filter(c => c.estado === 'activo').length}
              </p>
              <p className="text-sm text-muted-foreground">Clientes Activos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {clients.filter(c => c.estado === 'pausado').length}
              </p>
              <p className="text-sm text-muted-foreground">Pausados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{clients.length}</p>
              <p className="text-sm text-muted-foreground">Total Clientes</p>
            </CardContent>
          </Card>
        </motion.div>
        )}

        {/* New Client Form */}
        {showNewClientForm && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Nuevo Cliente</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setShowNewClientForm(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <CardDescription>Complete los datos del nuevo cliente. El nombre es obligatorio.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre de la empresa *</Label>
                    <Input
                      value={newClient.nombre}
                      onChange={(e) => setNewClient({ ...newClient, nombre: e.target.value })}
                      placeholder="Nombre de la empresa"
                      data-testid="new-client-nombre"
                    />
                  </div>
                  <div>
                    <Label>Persona de contacto</Label>
                    <Input
                      value={newClient.contacto}
                      onChange={(e) => setNewClient({ ...newClient, contacto: e.target.value })}
                      placeholder="Nombre del contacto"
                      data-testid="new-client-contacto"
                    />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      value={newClient.telefono}
                      onChange={(e) => setNewClient({ ...newClient, telefono: e.target.value })}
                      placeholder="+34 600 000 000"
                      data-testid="new-client-telefono"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      placeholder="email@empresa.com"
                      data-testid="new-client-email"
                    />
                  </div>
                  <div>
                    <Label>Servicio contratado</Label>
                    <select
                      value={newClient.servicio}
                      onChange={(e) => setNewClient({ ...newClient, servicio: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="new-client-servicio"
                    >
                      <option value="">Seleccionar servicio...</option>
                      {serviciosOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <select
                      value={newClient.estado}
                      onChange={(e) => setNewClient({ ...newClient, estado: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="new-client-estado"
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                  <div>
                    <Label>Fecha de inicio</Label>
                    <Input
                      type="date"
                      value={newClient.fechaInicio}
                      onChange={(e) => setNewClient({ ...newClient, fechaInicio: e.target.value })}
                      data-testid="new-client-fecha"
                    />
                  </div>
                </div>
                <div>
                  <Label>Observaciones</Label>
                  <Textarea
                    value={newClient.observaciones}
                    onChange={(e) => setNewClient({ ...newClient, observaciones: e.target.value })}
                    placeholder="Notas adicionales sobre el cliente..."
                    rows={3}
                    data-testid="new-client-observaciones"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowNewClientForm(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleCreateClient} 
                    disabled={creatingClient || !newClient.nombre.trim()}
                    data-testid="create-client-submit"
                  >
                    {creatingClient ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creando...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Crear Cliente
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Search */}
        <motion.div variants={itemVariants}>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </motion.div>

        {/* Lista de Clientes */}
        <motion.div variants={itemVariants}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredClientes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground opacity-50 mb-4" />
                <p className="text-muted-foreground">No hay clientes. Crea el primero.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredClientes.map((cliente) => (
                <Card
                  key={cliente._id || cliente.id}
                  className="card-hover cursor-pointer relative group"
                  onClick={() => setSelectedCliente(cliente)}
                  data-testid={`client-card-${cliente._id || cliente.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-14 h-14">
                        <AvatarFallback className="bg-primary/10 text-primary text-lg">
                          {cliente.nombre?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{cliente.nombre}</h3>
                            <PortalAccessBadge access={cliente.portal_access} id={cliente._id} />
                            {cliente.isDemo && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
                                Demo
                              </Badge>
                            )}
                          </div>
                          <Badge className={estadoConfig[cliente.estado]?.color || 'bg-gray-100'}>
                            {estadoConfig[cliente.estado]?.label || cliente.estado}
                          </Badge>
                        </div>
                        {cliente.contacto && (
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium">Contacto:</span> {cliente.contacto}
                          </p>
                        )}
                        {cliente.servicio && (
                          <Badge variant="outline" className="text-xs mt-1">{cliente.servicio}</Badge>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {cliente.telefono && (
                            <span>{cliente.telefono}</span>
                          )}
                          {cliente.email && (
                            <span>{cliente.email}</span>
                          )}
                          {cliente.fechaInicio && (
                            <span>Desde: {new Date(cliente.fechaInicio).toLocaleDateString('es-ES')}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setSelectedCliente(cliente); }}
                          data-testid={`view-detail-${cliente._id || cliente.id}`}
                        >
                          Ver detalle
                        </Button>
                        <button
                          onClick={(e) => handleDeleteClient(cliente._id || cliente.id, e)}
                          className="p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 rounded text-red-500"
                          data-testid={`delete-client-${cliente._id || cliente.id}`}
                        >
                          {deletingClient === (cliente._id || cliente.id) ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
    );
  }

  // Vista de detalle del cliente
  const sections = clientData?.sections || {};

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="client-detail-page"
    >
      {/* Header con botón volver */}
      <motion.div variants={itemVariants} className="flex items-center gap-4">
        <Button variant="outline" onClick={() => { setSelectedCliente(null); setClientData(null); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{selectedCliente.nombre}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={estadoConfig[selectedCliente.estado]?.color || 'bg-gray-100'}>
              {estadoConfig[selectedCliente.estado]?.label || selectedCliente.estado}
            </Badge>
            {selectedCliente.servicio && <Badge variant="outline" data-testid="client-header-servicio">{selectedCliente.servicio}</Badge>}
          </div>
          <PaymentConflictWarning clientData={clientData} />
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setEditClientDraft({
              nombre: selectedCliente.nombre || '',
              contacto: selectedCliente.contacto || '',
              telefono: selectedCliente.telefono || '',
              email: selectedCliente.email || '',
              servicio: selectedCliente.servicio || '',
              estado: selectedCliente.estado || 'activo',
              fechaInicio: selectedCliente.fechaInicio || '',
              observaciones: selectedCliente.observaciones || '',
              web: selectedCliente.web || '',
              direccion: selectedCliente.direccion || '',
              historial_breve: selectedCliente.historial_breve || '',
              valor_contrato: selectedCliente.valor_contrato ?? '',
              instagram: selectedCliente.instagram || '',
              discord_webhook_url: selectedCliente.discord_webhook_url || '',
              discord_ads_webhook_url: selectedCliente.discord_ads_webhook_url || '',
            });
            setEditClientOpen(true);
            setDiscordTest(null);
            setDiscordAdsTest(null);
          }}
          data-testid="edit-client-btn"
        >
          <PenTool className="w-4 h-4 mr-2" />
          Editar ficha
        </Button>
        {isCEO && <ViewClientPortalButton clientId={selectedCliente._id || selectedCliente.id} />}
        <Button
          variant="default"
          onClick={async () => {
            if (!selectedCliente.email) {
              toast.error('Añade un email a la ficha antes de invitar');
              return;
            }
            setInviting(true);
            try {
              const cid = selectedCliente._id || selectedCliente.id;
              const w = await clientWelcomeAPI.getClient(cid).catch(() => null);
              if (w && !w.discord_invite_url
                && !window.confirm('Falta el enlace de invitación de Discord: el cliente no verá ese bloque en su Bienvenida. ¿Invitar igualmente?')) {
                return;
              }
              const res = await serviceDeliveryAPI.inviteToPortal(cid);
              // Build absolute link if backend returned a relative one
              if (res && res.setup_link && res.setup_link.startsWith('/')) {
                res.setup_link = `${window.location.origin}${res.setup_link}`;
              }
              setInviteResult(res);
            } catch (e) {
              toast.error(e?.message || 'Error invitando al portal');
            } finally {
              setInviting(false);
            }
          }}
          disabled={inviting || selectedCliente.estado === 'inactivo'}
          title={selectedCliente.estado === 'inactivo' ? 'Cliente dado de baja: reactívalo para invitarlo' : undefined}
          data-testid="invite-client-btn"
        >
          <Mail className="w-4 h-4 mr-2" />
          {inviting ? 'Invitando...' : 'Invitar al portal'}
        </Button>
      </motion.div>

      <ClientArchiveControls client={selectedCliente} onChanged={(c) => {
        setSelectedCliente(c);
        loadClients();
      }} />

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar de secciones */}
        <div className="col-span-3">
          <Card className="sticky top-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Secciones</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <div className="space-y-1 p-2">
                  {seccionesCliente.filter((s) => !s.ceoOnly || isCEO).map((seccion) => {
                    const Icon = seccion.icon;
                    return (
                      <button
                        key={seccion.id}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeSection === seccion.id
                            ? 'bg-primary text-white'
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => setActiveSection(seccion.id)}
                        data-testid={`client-section-${seccion.id}`}
                      >
                        <Icon className="w-4 h-4" />
                        {seccion.nombre}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Contenido de la sección */}
        <div className="col-span-9">
          {!clientData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* BRIEFING */}
              {activeSection === 'briefing' && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Briefing</CardTitle>
                      {(() => {
                        const st = clientData.briefing_status || 'pendiente';
                        const meta = BRIEFING_STATUS[st] || BRIEFING_STATUS.pendiente;
                        return <Badge className={meta.color} variant="outline" data-testid="briefing-status-badge">{meta.label}</Badge>;
                      })()}
                    </div>
                    <CardDescription>Sincronizado con el portal del cliente. Tanto el cliente como el equipo pueden rellenarlo y editarlo.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <BriefingForm
                      data={clientData.briefing_data || {}}
                      paymentMethod={clientData.forma_pago || ''}
                      status={clientData.briefing_status || 'pendiente'}
                      onSetStatus={async (s) => {
                        await serviceDeliveryAPI.setBriefingStatus(clientData._id || clientData.id, s);
                        await loadClientDetail(clientData._id || clientData.id);
                        toast.success(s === 'completado' ? '¡Briefing marcado como completado!' : 'Estado actualizado');
                      }}
                      onRemind={async () => {
                        const res = await serviceDeliveryAPI.remindBriefing(clientData._id || clientData.id);
                        const parts = [];
                        if (res?.email_sent) parts.push('email');
                        if (res?.discord_sent) parts.push('Discord');
                        toast.success(parts.length ? `Recordatorio enviado por ${parts.join(' y ')}` : 'Recordatorio enviado');
                      }}
                      onUploadFile={(f) => serviceDeliveryAPI.uploadBriefingFile(clientData._id || clientData.id, f)}
                      onExtractFromFile={(f, schema) => serviceDeliveryAPI.extractBriefingFromFile(clientData._id || clientData.id, f, schema)}
                      buildFileUrl={briefingFileUrl}
                      hiddenSections={isCEO ? [] : ['info_basica']}
                      onSaveSection={async (section, data) => {
                        await serviceDeliveryAPI.saveBriefing(clientData._id || clientData.id, { section, data });
                        await loadClientDetail(clientData._id || clientData.id);
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {/* PLAN DE SOCIAL MEDIA: solo el PDF (histórico), visible también en el portal del cliente → Documentos */}
              {activeSection === 'plan-social-media' && (
                <Card data-testid="plan-social-media-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-primary" />Plan de Social Media</CardTitle>
                    <CardDescription>Sube aquí el PDF del plan. Cada plan nuevo se añade sin borrar los anteriores; el más reciente es el vigente. El cliente lo ve y lo descarga en su portal → Documentos. Máx. 100 MB.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FileUploadSection
                      clientId={clientData._id || clientData.id}
                      sectionId="plan-social-media"
                      archivos={[...(sections.planSocialMedia?.archivos || [])].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))}
                      highlightFirst="Plan vigente"
                      onUploadComplete={() => loadClientDetail(clientData._id || clientData.id)}
                    />
                  </CardContent>
                </Card>
              )}

              {/* INFO CLIENTE */}
              {activeSection === 'info' && isCEO && (
                <EditableSection
                  title="Información del Cliente"
                  icon={Globe}
                  sectionId="info"
                  data={sections.info}
                  onSave={handleSaveSection}
                  renderEdit={(data, setData) => (
                    <div className="space-y-4">
                      <div>
                        <Label>Descripción</Label>
                        <Textarea
                          value={data.descripcion || ''}
                          onChange={(e) => setData({ ...data, descripcion: e.target.value })}
                          placeholder="Descripción general del cliente"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Sector</Label>
                          <Input
                            value={data.sector || ''}
                            onChange={(e) => setData({ ...data, sector: e.target.value })}
                            placeholder="Ej: Tecnología, Retail, etc."
                          />
                        </div>
                        <div>
                          <Label>Tamaño</Label>
                          <Input
                            value={data.tamano || ''}
                            onChange={(e) => setData({ ...data, tamano: e.target.value })}
                            placeholder="Ej: Startup, PYME, Enterprise"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Ubicación</Label>
                        <Input
                          value={data.ubicacion || ''}
                          onChange={(e) => setData({ ...data, ubicacion: e.target.value })}
                          placeholder="Ciudad, País"
                        />
                      </div>
                      <div>
                        <Label>Notas</Label>
                        <Textarea
                          value={data.notas || ''}
                          onChange={(e) => setData({ ...data, notas: e.target.value })}
                          placeholder="Notas adicionales sobre el cliente"
                        />
                      </div>
                    </div>
                  )}
                >
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold mb-2">Descripción</h4>
                      <p>{sections.info?.descripcion || <span className="text-muted-foreground italic">Sin descripción</span>}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg border">
                        <h4 className="font-semibold mb-1 text-sm">Sector</h4>
                        <p>{sections.info?.sector || <span className="text-muted-foreground italic">-</span>}</p>
                      </div>
                      <div className="p-4 rounded-lg border">
                        <h4 className="font-semibold mb-1 text-sm">Tamaño</h4>
                        <p>{sections.info?.tamano || <span className="text-muted-foreground italic">-</span>}</p>
                      </div>
                      <div className="p-4 rounded-lg border">
                        <h4 className="font-semibold mb-1 text-sm">Ubicación</h4>
                        <p>{sections.info?.ubicacion || <span className="text-muted-foreground italic">-</span>}</p>
                      </div>
                    </div>
                    {sections.info?.notas && (
                      <div className="p-4 rounded-lg border">
                        <h4 className="font-semibold mb-2">Notas</h4>
                        <p className="text-sm whitespace-pre-wrap">{sections.info.notas}</p>
                      </div>
                    )}
                  </div>
                </EditableSection>
              )}

              {/* BUYER PERSONA */}
              {activeSection === 'buyer-persona' && (
                <BuyerPersonaSection clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null} />
              )}

              {/* ESTRATEGIA ADS */}
              {activeSection === 'estrategia-ads' && (
                <AdsStrategySection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {/* ESTRATEGIA CONTENIDO */}
              {activeSection === 'estrategia-contenido' && (
                <ContentStrategySection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {/* BRANDING / KIT DE MARCA */}
              {activeSection === 'branding' && (
                <BrandKitSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {/* Calendario de Contenido (Vista 1 del sistema de gestión de contenido) */}
              {activeSection === 'calendario' && (
                <ClientContentCalendar
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                  clientName={selectedCliente?.name || selectedCliente?.nombre || ''}
                  initialPostId={initialPostIdToOpen}
                  onInitialPostOpened={() => setInitialPostIdToOpen(null)}
                />
              )}

              {activeSection === 'negocio' && (
                <BusinessInfoSection clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null} />
              )}

              {activeSection === 'reuniones' && (
                <ClientMeetings
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                  clientName={selectedCliente?.name || selectedCliente?.nombre || ''}
                />
              )}

              {activeSection === 'prueba-social' && (
                <SocialProofSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'investigacion' && (
                <MarketResearchSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'estrategia-negocio' && (
                <BusinessStrategySection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'anuncios' && (
                <AdsCampaignsSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'contenido' && (
                <ContentLibrarySection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'copies' && (
                <CopiesSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'objetivos' && (
                <ObjectivesSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'informes' && (
                <ReportsSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                />
              )}

              {activeSection === 'incidencias-cobro' && isCEO && (
                <BillingIncidentsSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                  clientName={selectedCliente?.nombre || selectedCliente?.name || ''}
                />
              )}

              {activeSection === 'integraciones' && isCEO && clientData && (
                <ClientIntegrationsSection client={clientData} />
              )}

              {activeSection === 'drive' && (
                <DriveFoldersSection clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null} />
              )}

              {activeSection === 'checklists' && isCEO && selectedCliente && (
                <ClientChecklistsSection clientId={selectedCliente._id || selectedCliente.id} />
              )}

              {activeSection === 'ads_creativos' && isCEO && selectedCliente && (
                <AdsApprovalsManager clientId={selectedCliente._id || selectedCliente.id} />
              )}

              {activeSection === 'actividad' && isCEO && selectedCliente && (
                <ClientPortalActivity clientId={selectedCliente._id || selectedCliente.id} />
              )}

              {activeSection === 'bienvenida' && isCEO && (
                <ClientWelcomeSetup clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null} />
              )}

              {activeSection === 'acciones' && (
                <WeeklyActionsSection
                  clientId={(selectedCliente && (selectedCliente._id || selectedCliente.id)) || null}
                  clientName={selectedCliente?.nombre || selectedCliente?.name || ''}
                />
              )}

              {/* Placeholder para otras secciones */}
              {!['briefing', 'plan-social-media', 'info', 'negocio', 'buyer-persona', 'estrategia-ads', 'estrategia-contenido', 'branding', 'calendario', 'reuniones', 'prueba-social', 'investigacion', 'estrategia-negocio', 'anuncios', 'contenido', 'copies', 'objetivos', 'informes', 'incidencias-cobro', 'integraciones', 'acciones', 'drive', 'bienvenida', 'actividad', 'checklists', 'ads_creativos'].includes(activeSection) && (isCEO || activeSection !== 'especificaciones') && (
                <EditableSection
                  title={seccionesCliente.find(s => s.id === activeSection)?.nombre || 'Sección'}
                  icon={seccionesCliente.find(s => s.id === activeSection)?.icon || FileText}
                  sectionId={activeSection}
                  data={sections[activeSection.replace(/-([a-z])/g, (g) => g[1].toUpperCase())] || {}}
                  onSave={handleSaveSection}
                  renderEdit={(data, setData) => (
                    <div className="space-y-4">
                      <div>
                        <Label>Contenido</Label>
                        <Textarea
                          value={data.contenido || ''}
                          onChange={(e) => setData({ ...data, contenido: e.target.value })}
                          placeholder="Añade información para esta sección..."
                          rows={6}
                        />
                      </div>
                      <div>
                        <Label>Notas</Label>
                        <Textarea
                          value={data.notas || ''}
                          onChange={(e) => setData({ ...data, notas: e.target.value })}
                          placeholder="Notas adicionales..."
                          rows={4}
                        />
                      </div>
                    </div>
                  )}
                >
                  <div className="text-center py-12">
                    <Lightbulb className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">
                      Haz clic en "Editar" para añadir contenido a esta sección.
                    </p>
                  </div>
                </EditableSection>
              )}
            </>
          )}
        </div>
      </div>

      {/* Invite to Portal Result Dialog */}
      <Dialog open={!!inviteResult} onOpenChange={(o) => { if (!o) setInviteResult(null); }}>
        <DialogContent className="max-w-xl" data-testid="invite-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              {inviteResult?.email_sent ? 'Invitación enviada' : 'Invitación creada'}
            </DialogTitle>
          </DialogHeader>
          {inviteResult && (
            <div className="space-y-3 text-sm">
              {inviteResult.email_sent ? (
                <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
                  ✅ Email enviado correctamente a <span className="font-semibold">{inviteResult.email}</span>.
                  El cliente recibirá un enlace para crear su contraseña.
                </div>
              ) : (
                <>
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900">
                    ⚠️ El email automático no se pudo enviar (Resend en modo sandbox). El usuario está creado correctamente — solo necesitas enviarle este enlace manualmente:
                  </div>
                  {inviteResult.email_error && (
                    <details className="text-xs text-muted-foreground">
                      <summary className="cursor-pointer">Detalles técnicos</summary>
                      <p className="mt-1 break-words">{inviteResult.email_error}</p>
                    </details>
                  )}
                </>
              )}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Cliente</p>
                <p className="font-medium">{inviteResult.email}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Enlace para crear contraseña (válido 14 días)</p>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={inviteResult.setup_link || ''}
                    onFocus={(e) => e.target.select()}
                    className="text-xs font-mono"
                    data-testid="invite-link-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(inviteResult.setup_link || '');
                        toast.success('Enlace copiado');
                      } catch {
                        toast.error('No se pudo copiar — selecciona manualmente');
                      }
                    }}
                    data-testid="copy-invite-link-btn"
                  >
                    Copiar
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Pásaselo al cliente por WhatsApp, email manual, o el canal que prefieras.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInviteResult(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={editClientOpen} onOpenChange={setEditClientOpen}>
        <DialogContent className="max-w-2xl" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>Editar ficha de cliente</DialogTitle>
          </DialogHeader>
          {editClientDraft && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre / Empresa *</Label>
                  <Input value={editClientDraft.nombre} onChange={(e) => setEditClientDraft((d) => ({ ...d, nombre: e.target.value }))} data-testid="edit-nombre-input"
                    className={!editClientDraft.nombre?.trim() ? 'border-red-400' : ''} />
                  {!editClientDraft.nombre?.trim() && <p className="text-xs text-red-600 mt-1" data-testid="edit-nombre-error">El nombre es obligatorio</p>}
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={editClientDraft.estado} onValueChange={(v) => setEditClientDraft((d) => ({ ...d, estado: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="activo">Activo</SelectItem>
                      <SelectItem value="pausado">Pausado</SelectItem>
                      <SelectItem value="finalizado">Finalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Persona de contacto</Label>
                  <Input value={editClientDraft.contacto} onChange={(e) => setEditClientDraft((d) => ({ ...d, contacto: e.target.value }))} />
                </div>
                <div>
                  <Label>Servicio contratado</Label>
                  <select value={editClientDraft.servicio || ''} onChange={(e) => setEditClientDraft((d) => ({ ...d, servicio: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="edit-client-servicio">
                    <option value="">Seleccionar servicio...</option>
                    {editClientDraft.servicio && !serviciosOptions.includes(editClientDraft.servicio) && (
                      <option value={editClientDraft.servicio}>{editClientDraft.servicio} (texto antiguo: elige una opción)</option>
                    )}
                    {serviciosOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Teléfono</Label>
                  <Input value={editClientDraft.telefono} onChange={(e) => setEditClientDraft((d) => ({ ...d, telefono: e.target.value }))} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={editClientDraft.email} onChange={(e) => setEditClientDraft((d) => ({ ...d, email: e.target.value }))} data-testid="edit-email-input"
                    className={editEmailInvalid ? 'border-red-400' : ''} />
                  {editEmailInvalid && <p className="text-xs text-red-600 mt-1" data-testid="edit-email-error">El email no tiene un formato válido</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Fecha de inicio</Label>
                  <Input type="date" value={editClientDraft.fechaInicio} onChange={(e) => setEditClientDraft((d) => ({ ...d, fechaInicio: e.target.value }))} />
                </div>
                <div>
                  <Label>Dirección</Label>
                  <Input value={editClientDraft.direccion || ''} onChange={(e) => setEditClientDraft((d) => ({ ...d, direccion: e.target.value }))} data-testid="edit-client-direccion" />
                </div>
                <div>
                  <Label>Valor del contrato (€)</Label>
                  <Input type="number" value={editClientDraft.valor_contrato ?? ''} onChange={(e) => setEditClientDraft((d) => ({ ...d, valor_contrato: e.target.value }))} data-testid="edit-client-valor" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Historial breve</Label>
                  <Textarea value={editClientDraft.historial_breve || ''} onChange={(e) => setEditClientDraft((d) => ({ ...d, historial_breve: e.target.value }))} rows={2} data-testid="edit-client-historial" />
                </div>
                <div>
                  <Label>Web</Label>
                  <Input value={editClientDraft.web} onChange={(e) => setEditClientDraft((d) => ({ ...d, web: e.target.value }))} placeholder="https://..." />
                </div>
                <div>
                  <Label>Instagram</Label>
                  <Input value={editClientDraft.instagram} onChange={(e) => setEditClientDraft((d) => ({ ...d, instagram: e.target.value }))} placeholder="@usuario" />
                </div>
              </div>
              <div>
                <Label>Observaciones</Label>
                <Textarea rows={3} value={editClientDraft.observaciones} onChange={(e) => setEditClientDraft((d) => ({ ...d, observaciones: e.target.value }))} />
              </div>
              <div>
                <Label className="flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.099.246.198.373.291a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                  Webhook de Discord (canal de contenido)
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={editClientDraft.discord_webhook_url || ''}
                    onChange={(e) => { setEditClientDraft((d) => ({ ...d, discord_webhook_url: e.target.value })); setDiscordTest(null); }}
                    placeholder="https://discord.com/api/webhooks/..."
                    data-testid="edit-discord-webhook-input"
                  />
                  <Button type="button" variant="outline" className="shrink-0" disabled={discordTest === 'busy' || !(editClientDraft.discord_webhook_url || '').trim()}
                    onClick={async () => {
                      setDiscordTest('busy');
                      try { setDiscordTest(await serviceDeliveryAPI.testDiscordWebhook(selectedCliente._id || selectedCliente.id, editClientDraft.discord_webhook_url)); }
                      catch (e) { setDiscordTest({ ok: false, detail: e.message }); }
                    }}
                    data-testid="edit-discord-webhook-test-btn">
                    {discordTest === 'busy' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Probar
                  </Button>
                </div>
                {discordTest && discordTest !== 'busy' && (
                  <p className={`text-xs mt-1 font-medium ${discordTest.ok ? 'text-emerald-700' : 'text-red-600'}`} data-testid="edit-discord-webhook-test-result">
                    {discordTest.ok ? '✓ ' : '✗ '}{discordTest.detail}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">
                  Canal de contenido de este cliente (lo lleva Karmele). Por aquí le llegan: publicaciones para aprobar, recordatorios y reuniones. (Ajustes del canal → Integraciones → Webhooks → Nuevo webhook → Copiar URL)
                </p>
              </div>
              <div>
                <Label>Webhook de Discord (canal de ads)</Label>
                <div className="flex gap-2">
                  <Input
                    value={editClientDraft.discord_ads_webhook_url || ''}
                    onChange={(e) => { setEditClientDraft((d) => ({ ...d, discord_ads_webhook_url: e.target.value })); setDiscordAdsTest(null); }}
                    placeholder="https://discord.com/api/webhooks/..."
                    data-testid="edit-discord-ads-webhook-input"
                  />
                  <Button type="button" variant="outline" className="shrink-0" disabled={discordAdsTest === 'busy' || !(editClientDraft.discord_ads_webhook_url || '').trim()}
                    onClick={async () => {
                      setDiscordAdsTest('busy');
                      try { setDiscordAdsTest(await serviceDeliveryAPI.testDiscordWebhook(selectedCliente._id || selectedCliente.id, editClientDraft.discord_ads_webhook_url)); }
                      catch (e) { setDiscordAdsTest({ ok: false, detail: e.message }); }
                    }}
                    data-testid="edit-discord-ads-webhook-test-btn">
                    {discordAdsTest === 'busy' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Probar
                  </Button>
                </div>
                {discordAdsTest && discordAdsTest !== 'busy' && (
                  <p className={`text-xs mt-1 font-medium ${discordAdsTest.ok ? 'text-emerald-700' : 'text-red-600'}`} data-testid="edit-discord-ads-webhook-test-result">
                    {discordAdsTest.ok ? '✓ ' : '✗ '}{discordAdsTest.detail}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">
                  Canal de ads de este cliente (lo lleva Marta). Por aquí le llegan: creativos de campaña para aprobar e informe de Ads publicado. Si lo dejas vacío, esos avisos van al canal privado de Marta (Aprobación de creativos), nunca al de contenido.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditClientOpen(false)} disabled={savingEdit}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (!editClientDraft || !selectedCliente) return;
                setSavingEdit(true);
                try {
                  const cid = selectedCliente._id || selectedCliente.id;
                  await serviceDeliveryAPI.updateClient(cid, { ...editClientDraft, valor_contrato: editClientDraft.valor_contrato === '' || editClientDraft.valor_contrato == null ? null : Number(editClientDraft.valor_contrato) });
                  setSelectedCliente({ ...selectedCliente, ...editClientDraft });
                  // Refresh clients list so the card reflects the change
                  loadClients();
                  toast.success('Ficha actualizada');
                  setEditClientOpen(false);
                } catch (e) {
                  toast.error(e?.message || 'Error guardando la ficha');
                } finally {
                  setSavingEdit(false);
                }
              }}
              disabled={savingEdit || !editClientDraft?.nombre?.trim() || editEmailInvalid}
              data-testid="save-edit-client-btn"
            >
              {savingEdit ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
